<template>
  <LineChartGenerator
    :chart-options="chartOptions"
    :chart-data="chartData"
    :chart-id="chartId"
    :dataset-id-key="datasetIdKey"
    :plugins="pluginsAll"
    :css-classes="cssClasses"
    :styles="styles"
    :width="width"
    :height="height"
  />
</template>

<script>
import { Line as LineChartGenerator } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  Filler
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, LineElement, LinearScale, CategoryScale, PointElement, Filler)

/* ---------- Frozen plugins ---------- */
const shadowGlowPlugin = Object.freeze({
  id: 'shadowGlow',
  beforeDatasetsDraw(chart, args, opts) {
    const { ctx } = chart
    ctx.save()
    ctx.shadowColor = opts?.shadowColor || 'rgba(0,0,0,0.25)'
    ctx.shadowBlur = opts?.shadowBlur || 12
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 6
  },
  afterDatasetsDraw(chart) {
    chart.ctx.restore()
  }
})

const verticalLinesPlugin = Object.freeze({
  id: 'verticalLines',
  afterDraw(chart, _args, opts) {
    const { ctx, chartArea, scales } = chart
    const xScale = scales?.x
    if (!chartArea || !xScale) return

    // 1) From datasets
    const fromDatasets = (chart.data?.datasets || [])
      .flatMap((ds, i) => (ds.markers || []).map(m => ({ ...m, _ds: i })))

    // 2) Global markers attached to data (set from prop)
    const globalMarkers = chart.data?._globalMarkers || []

    // 3) Optional fallback (style-only in options; avoid positions here normally)
    const fromOptions = (opts?.positions || []).map(m => ({ ...m }))

    const markers = [...fromDatasets, ...globalMarkers, ...fromOptions]
    if (!markers.length) return

    const top = chartArea.top
    const bottom = chartArea.bottom
    const isCategory = xScale.type === 'category'
    const labelsArr = chart.data?.labels || []

    ctx.save()
    ctx.strokeStyle = opts?.color || 'rgba(255, 99, 132, 0.9)'
    ctx.lineWidth = opts?.lineWidth ?? 2
    ctx.setLineDash(opts?.dash || [])

    markers.forEach(m => {
      const text = m.text
      let xPos

      if (isCategory) {
        let idx = -1
        if (typeof m.index === 'number') idx = m.index
        else if (typeof m.label === 'string') idx = labelsArr.indexOf(m.label)
        if (idx < 0) return
        xPos = xScale.getPixelForTick(idx)
      } else {
        const val = m.value ?? m.index ?? m.label
        xPos = xScale.getPixelForValue(val)
      }

      if (!Number.isFinite(xPos) || xPos < chartArea.left || xPos > chartArea.right) return

      // Line
      ctx.beginPath()
      ctx.moveTo(xPos, top)
      ctx.lineTo(xPos, bottom)
      ctx.stroke()

      // Optional cap
      if (opts?.capHeight) {
        ctx.beginPath()
        ctx.moveTo(xPos - 4, top)
        ctx.lineTo(xPos + 4, top)
        ctx.stroke()
      }

      // Label
      if (opts?.showLabels && text) {
        ctx.save()
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.font = opts?.labelFont || '500 12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial'
        ctx.fillStyle = opts?.labelColor || ctx.strokeStyle
        ctx.fillText(text, xPos, top - 4)
        ctx.restore()
      }
    })

    ctx.restore()
  }
})

/* ---------- Helpers ---------- */
function hexToRgba(hex, a = 1) {
  const c = hex.replace('#', '')
  const full = c.length === 3 ? c.split('').map(ch => ch + ch).join('') : c
  const bigint = parseInt(full, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

export default {
  name: 'MemlistLineChart',
  components: { LineChartGenerator },

  props: {
    /* existing */
    chartId: { type: String, default: 'line-chart' },
    datasetIdKey: { type: String, default: 'label' },
    width: { type: Number, default: 400 },
    height: { type: Number, default: 400 },
    cssClasses: { type: String, default: '' },
    styles: { type: Object, default: () => ({}) },
    plugins: { type: Array, default: () => [] },

    /* NEW — data props */
    labels: { type: Array, default: () => [] },
    datasets: {
      type: Array,
      default: () => [
        // Example structure:
        // { label: 'Series A', data: [1,2,3], color: '#4f46e5', fill: true, tension: 0.4, markers: [{label:'March', text:'Deployed'}] }
      ]
    },
    markers: {
      // Global markers that apply to the whole chart
      type: Array,
      default: () => [
        // { label: 'June', text: 'Release' }, { index: 1, text: 'Note' }
      ]
    },

    /* NEW — options props */
    title: { type: String, default: '' },
    showLegend: { type: Boolean, default: true },
    smooth: { type: Boolean, default: true },   // default line smoothing
    fill: { type: Boolean, default: true },     // default area fill
    yBeginAtZero: { type: Boolean, default: true },
    yTickFormatter: { type: Function, default: (v) => `${v}` },

    /* X scale type if you need time/linear later */
    xScaleType: { type: String, default: 'category' }, // 'category' | 'linear' | 'time'

    /* Style knobs for markers */
    verticalLinesStyle: {
      type: Object,
      default: () => ({
        color: 'rgba(255, 99, 132, 0.9)',
        lineWidth: 2,
        dash: [4, 4],
        capHeight: true,
        showLabels: true,
        labelColor: '#ff6384'
      })
    }
  },

  computed: {
    pluginsAll() {
      const ext = Array.isArray(this.plugins) ? this.plugins : []
      return Object.freeze([shadowGlowPlugin, verticalLinesPlugin, ...ext])
    },

    chartData() {
      const primary =
        getComputedStyle(document.documentElement).getPropertyValue('--ml-primary')?.trim() || '#4f46e5'

        // Map incoming datasets -> Chart.js datasets
        const mapped = (this.datasets || []).map((d, i) => {
          const color = d.color || primary
          return {
            label: d.label ?? `Series ${i + 1}`,
            data: Array.isArray(d.data) ? d.data : [],
            borderWidth: 3,
            tension: typeof d.tension === 'number' ? d.tension : (this.smooth ? 0.4 : 0),
            pointRadius: 3,
            pointHoverRadius: 6,
            pointHitRadius: 20,
            fill: typeof d.fill === 'boolean' ? d.fill : this.fill,

            // Make the line a solid color instead of gradient fade
            borderColor: color,

            // Keep the fade only for fill area
            backgroundColor: (ctx) => this.makeGradient(ctx, color, 0.4),

            markers: Array.isArray(d.markers) ? d.markers : []
          }
        });

      return {
        labels: Array.isArray(this.labels) ? this.labels : [],
        datasets: mapped,
        // Global markers live alongside data so the plugin can read them safely
        _globalMarkers: Array.isArray(this.markers) ? this.markers : []
      }
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 800, easing: 'easeOutQuart' },
        plugins: {
          legend: this.showLegend
            ? { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, padding: 16 } }
            : { display: false },
          title: this.title
            ? { display: true, text: this.title, padding: { top: 8, bottom: 16 }, font: { size: 16, weight: '600' } }
            : { display: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            padding: 12,
            displayColors: false,
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}`
            }
          },
          shadowGlow: { shadowColor: 'rgba(0,0,0,0.2)', shadowBlur: 10 },
          // Only style knobs here (no positions)
          verticalLines: Object.freeze({ ...this.verticalLinesStyle })
        },
        elements: { line: { capBezierPoints: true }, point: { hoverBorderWidth: 2 } },
        scales: {
          x: {
            type: this.xScaleType,
            grid: { display: false },
            ticks: { padding: 8 }
          },
          y: {
            beginAtZero: this.yBeginAtZero,
            grid: { color: 'rgba(0,0,0,0.05)', tickLength: 0 },
            ticks: { padding: 8, callback: this.yTickFormatter }
          }
        }
      }
    }
  },

  methods: {
    makeGradient(ctx, base, alpha = 1) {
      const { chart } = ctx
      const { ctx: canvas, chartArea } = chart || {}
      if (!chartArea) return base
      const g = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
      g.addColorStop(0, hexToRgba(base, alpha))
      g.addColorStop(1, hexToRgba(base, 0.15)) // leave slight opacity at bottom
      return g
    }

  }
}
</script>

<style scoped>
:deep(canvas) { border-radius: 12px; }
</style>
